import { useState, type FormEvent } from "react";
import { categories } from "@/mocks/forecast";

const FORM_ENDPOINT = "https://readdy.ai/api/form/daildp3p14a1h3mr765g";

export default function SubscribeForm() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const payload = new FormData(form);

    const honeypot = String(payload.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }
    payload.delete("website_alt");

    const email = String(payload.get("email") || "").trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setFormError("请填写有效的邮箱地址，方便我们发送蹲守提醒。");
      return;
    }

    setStatus("loading");
    setFormError("");

    try {
      const body = new URLSearchParams();
      payload.forEach((value, key) => {
        body.append(key, String(value));
      });

      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText) as Record<string, unknown>;
      } catch {
        parsed = null;
      }

      const meta = (parsed?.meta ?? {}) as Record<string, unknown>;
      const code = typeof parsed?.code === "string" ? parsed.code : "";
      const serverMsgRaw = meta.message || parsed?.message || meta.detail || responseText;
      const serverMsg = typeof serverMsgRaw === "string" ? serverMsgRaw : "";
      const isSpam = serverMsg.toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后再试。");
    }
  };

  return (
    <form
      id="subscribe-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="scroll-mt-24 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <div className="flex flex-col gap-3 sm:flex-row">
        <label className="flex-1">
          <span className="sr-only">邮箱地址</span>
          <input
            type="email"
            name="email"
            required
            autoComplete="email"
            placeholder="输入邮箱，接收蹲守提醒"
            className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
        </label>
        <label className="sm:w-40">
          <span className="sr-only">关注分类</span>
          <select
            name="category"
            defaultValue="all"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-3 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
          >
            <option value="all">全部分类</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.name}>
                {cat.name}
              </option>
            ))}
          </select>
        </label>
        <button
          type="submit"
          disabled={status === "loading"}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {status === "loading" ? (
            <>
              <i className="ri-loader-4-line animate-spin" />
              提交中
            </>
          ) : (
            <>
              <i className="ri-notification-3-line" />
              订阅提醒
            </>
          )}
        </button>
      </div>

      <input
        className="form-extra-field"
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-3 min-h-[20px] text-xs">
        {status === "success" && (
          <p className="flex items-center gap-2 text-accent-700">
            <i className="ri-checkbox-circle-line" />
            订阅成功！新的前瞻发布时，我们会给你发送一封简短提醒。
          </p>
        )}
        {status === "error" && formError && (
          <p className="flex items-center gap-2 text-primary-700">
            <i className="ri-error-warning-line" />
            {formError}
          </p>
        )}
        {status === "idle" && (
          <p className="text-foreground-500">我们不会高频打扰，也不会将你的邮箱用于其他用途。</p>
        )}
      </div>
    </form>
  );
}